import { useEffect, useRef, useState } from "react";

/**
 * Animated SVG sparkline. Draws itself in on mount using stroke-dashoffset.
 * Pass an array of numeric `points` (any scale — they'll be normalised).
 */
export const Sparkline = ({
    points = [],
    width = 400,
    height = 120,
    strokeColor = "#2dd4bf",
    fillColor = "rgba(45, 212, 191, 0.18)",
    strokeWidth = 2,
    duration = 1800,
    delay = 200,
    className = "",
}) => {
    const pathRef = useRef(null);
    const [len, setLen] = useState(0);

    useEffect(() => {
        if (pathRef.current) setLen(pathRef.current.getTotalLength());
    }, [points, width, height]);

    if (!points.length) return null;

    // Normalise
    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min || 1;
    const step = width / Math.max(points.length - 1, 1);
    const pad = strokeWidth + 2;
    const yFor = (v) => height - pad - ((v - min) / range) * (height - pad * 2);

    // Smooth curve via cubic bezier midpoints
    const pts = points.map((v, i) => [i * step, yFor(v)]);
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[i + 1];
        const midX = (x1 + x2) / 2;
        d += ` Q ${x1} ${y1}, ${midX} ${(y1 + y2) / 2}`;
        d += ` T ${x2} ${y2}`;
    }
    const areaD = `${d} L ${width} ${height} L 0 ${height} Z`;
    const last = pts[pts.length - 1];

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            className={className}
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={fillColor} stopOpacity="1" />
                    <stop offset="100%" stopColor={fillColor} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path
                d={areaD}
                fill="url(#spark-fill)"
                style={{
                    opacity: 0,
                    animation: `sparkFadeIn 800ms ease-out ${delay + duration - 400}ms forwards`,
                }}
            />
            <path
                ref={pathRef}
                d={d}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                    strokeDasharray: len,
                    strokeDashoffset: len,
                    animation: `sparkDraw ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms forwards`,
                }}
            />
            <circle
                cx={last[0]}
                cy={last[1]}
                r={4}
                fill={strokeColor}
                style={{
                    opacity: 0,
                    animation: `sparkFadeIn 400ms ease-out ${delay + duration - 100}ms forwards`,
                }}
            />
            <circle
                cx={last[0]}
                cy={last[1]}
                r={10}
                fill={strokeColor}
                opacity="0.3"
                style={{
                    opacity: 0,
                    transformOrigin: `${last[0]}px ${last[1]}px`,
                    animation: `sparkPulse 1600ms ease-out ${delay + duration}ms infinite`,
                }}
            />
        </svg>
    );
};

export default Sparkline;
